import { useState, useEffect } from "react";
import Typography from "../Typography";
import { getPresignedDownloadUrl } from "../../services/api";

function getContentTypeFromFilename(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "pdf":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
}

type Props = {
  name: string;
  /** File object for newly uploaded attachments (enables local object URL preview). */
  file?: File;
  /** S3 key for saved attachments (fetches a presigned GET URL for preview). */
  s3Key?: string;
  uploading?: boolean;
  error?: boolean;
  /** If provided, a remove button is rendered. */
  onRemove?: () => void;
  /** Optional label shown before the filename (e.g. expense date). */
  label?: string;
};

export default function AttachmentItem({
  name,
  file,
  s3Key,
  uploading = false,
  error = false,
  onRemove,
  label,
}: Props) {
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFetchError, setPreviewFetchError] = useState(false);
  const [imgError, setImgError] = useState(false);

  const contentType = file?.type || getContentTypeFromFilename(name);
  const isImage = contentType.startsWith("image/");
  const isPdf = contentType === "application/pdf";
  const isPreviewSupported = isImage || isPdf;

  useEffect(() => {
    if (!showPreview) {
      setPreviewUrl(null);
      setPreviewFetchError(false);
      setImgError(false);
      return;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    if (s3Key) {
      setPreviewLoading(true);
      setPreviewFetchError(false);
      let cancelled = false;
      getPresignedDownloadUrl(s3Key)
        .then((url) => {
          if (!cancelled) setPreviewUrl(url);
        })
        .catch(() => {
          if (!cancelled) setPreviewFetchError(true);
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [showPreview, file, s3Key]);

  return (
    <li className="AttachmentItem">
      <div className="AttachmentItem__row">
        {label && <span className="AttachmentItem__label">{label} —</span>}
        <span className="AttachmentItem__name">
          {name}
          {uploading && " (caricamento...)"}
          {error && " ⚠ errore"}
        </span>
        <div className="AttachmentItem__actions">
          {!uploading && !error && (
            <button
              type="button"
              className="AttachmentItem__preview-btn"
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? "Nascondi" : "Preview"}
            </button>
          )}
          {onRemove && !uploading && (
            <button
              type="button"
              className="AttachmentItem__remove-btn"
              onClick={onRemove}
              aria-label="Rimuovi allegato"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {showPreview && (
        <div className="AttachmentItem__preview">
          {previewLoading && (
            <Typography variant="body2">Caricamento preview...</Typography>
          )}
          {previewFetchError && (
            <Typography variant="body2">
              Impossibile caricare la preview. Verifica i permessi S3 e riprova.
            </Typography>
          )}
          {!previewLoading && !previewFetchError && isImage && previewUrl && (
            <>
              {!imgError ? (
                <img
                  src={previewUrl}
                  alt={name}
                  className="AttachmentItem__img"
                  onError={() => setImgError(true)}
                />
              ) : (
                <Typography variant="body2">
                  Impossibile visualizzare l'immagine. Verifica i permessi S3.
                </Typography>
              )}
            </>
          )}
          {!previewLoading && !previewFetchError && isPdf && previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="AttachmentItem__pdf-link"
            >
              Apri PDF
            </a>
          )}
          {!previewLoading && !previewFetchError && !isPreviewSupported && (
            <Typography variant="body2">
              Preview non supportata per questo tipo di file.
            </Typography>
          )}
        </div>
      )}
    </li>
  );
}
